'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ScoreGauge from '@/components/tools/ScoreGauge';
import ProgressBar from '@/components/tools/ProgressBar';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Finding {
  type: 'positive' | 'negative' | 'neutral';
  text: string;
}

interface CategoryResult {
  name: string;
  score: number;
  findings: Finding[];
}

interface GradeResult {
  overall: number;
  letterGrade: string;
  categories: CategoryResult[];
}

/* ------------------------------------------------------------------ */
/*  Word lists                                                         */
/* ------------------------------------------------------------------ */

const STRONG_ACTION_VERBS = [
  'achieved', 'accelerated', 'accomplished', 'administered', 'advanced',
  'analyzed', 'architected', 'automated', 'boosted', 'built', 'captured',
  'championed', 'consolidated', 'created', 'cultivated', 'decreased',
  'delivered', 'designed', 'developed', 'directed', 'drove', 'earned',
  'eliminated', 'enabled', 'engineered', 'established', 'exceeded',
  'executed', 'expanded', 'expedited', 'facilitated', 'formulated',
  'founded', 'generated', 'grew', 'headed', 'identified', 'implemented',
  'improved', 'increased', 'influenced', 'initiated', 'innovated',
  'introduced', 'launched', 'led', 'leveraged', 'managed', 'maximized',
  'mentored', 'modernized', 'negotiated', 'optimized', 'orchestrated',
  'outperformed', 'overhauled', 'partnered', 'piloted', 'pioneered',
  'produced', 'programmed', 'propelled', 'ranked', 'rebuilt', 'reduced',
  'reengineered', 'reorganized', 'resolved', 'restructured', 'revamped',
  'scaled', 'secured', 'simplified', 'spearheaded', 'streamlined',
  'strengthened', 'surpassed', 'transformed', 'tripled', 'unified',
  'upgraded',
];

const WEAK_PHRASES = [
  'responsible for', 'duties included', 'helped with', 'assisted in',
  'worked on', 'involved in', 'participated in', 'tasked with',
  'in charge of', 'handled', 'dealt with',
];

const BUZZWORDS = [
  'synergy', 'synergies', 'leverage', 'paradigm', 'holistic', 'bandwidth',
  'circle back', 'move the needle', 'thought leader', 'best-in-class',
  'game-changer', 'rockstar', 'ninja', 'guru', 'wizard', 'unicorn',
  'wear many hats', 'go-getter', 'self-starter', 'team player',
  'detail-oriented', 'hard worker', 'fast learner', 'people person',
  'results-oriented', 'dynamic', 'proactive',
];

const SECTION_HEADERS = [
  'experience', 'work experience', 'professional experience', 'employment',
  'education', 'skills', 'technical skills', 'core competencies',
  'summary', 'professional summary', 'objective', 'career objective',
  'projects', 'certifications', 'certificates', 'awards', 'honors',
  'volunteer', 'volunteering', 'publications', 'languages',
];

const EXPERIENCE_HEADERS = [
  'experience', 'work experience', 'professional experience', 'employment',
  'employment history', 'work history',
];

const EDUCATION_HEADERS = [
  'education', 'academic background', 'academic',
];

const SKILLS_HEADERS = [
  'skills', 'technical skills', 'core competencies', 'competencies',
  'technologies', 'tools', 'proficiencies',
];

/* ------------------------------------------------------------------ */
/*  Scoring functions                                                   */
/* ------------------------------------------------------------------ */

function scoreImpact(text: string): CategoryResult {
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const findings: Finding[] = [];

  // Check for numbers / metrics
  const linesWithNumbers = lines.filter((l) => /\d+[%$kKmMbB]|\$\d|#\d|\d+\s*%|\d+x|\d+\+/.test(l));
  const metricsRatio = lines.length > 0 ? linesWithNumbers.length / lines.length : 0;

  if (metricsRatio > 0.3) {
    findings.push({ type: 'positive', text: `Strong use of metrics -- ${linesWithNumbers.length} lines contain quantified results.` });
  } else if (metricsRatio > 0.1) {
    findings.push({ type: 'neutral', text: `Some metrics found (${linesWithNumbers.length} lines). Try to quantify more achievements with numbers, percentages, or dollar amounts.` });
  } else {
    findings.push({ type: 'negative', text: 'Very few quantified results found. Add numbers, percentages, and dollar amounts to demonstrate impact (e.g., "Increased revenue by 25%").' });
  }

  // Check for action verbs at line starts
  const actionVerbLines = lines.filter((line) => {
    const firstWord = line.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '');
    return STRONG_ACTION_VERBS.includes(firstWord);
  });
  const actionVerbRatio = lines.length > 0 ? actionVerbLines.length / lines.length : 0;

  if (actionVerbRatio > 0.25) {
    findings.push({ type: 'positive', text: `Great use of strong action verbs (${actionVerbLines.length} bullet points start with power verbs).` });
  } else if (actionVerbRatio > 0.1) {
    findings.push({ type: 'neutral', text: `Some action verbs detected. Try starting more bullet points with verbs like "achieved," "built," "delivered," or "increased."` });
  } else {
    findings.push({ type: 'negative', text: 'Few strong action verbs found. Begin each bullet point with a power verb (achieved, built, delivered, led, etc.).' });
  }

  // Check for result-oriented language
  const resultWords = ['result', 'achieved', 'impact', 'outcome', 'success', 'improved', 'increased', 'decreased', 'reduced', 'saved', 'generated', 'grew', 'boosted'];
  const resultCount = resultWords.reduce((count, word) => {
    const regex = new RegExp(`\\b${word}\\w*\\b`, 'gi');
    return count + (text.match(regex)?.length || 0);
  }, 0);

  if (resultCount >= 5) {
    findings.push({ type: 'positive', text: 'Resume language is results-focused -- this is what recruiters look for.' });
  } else {
    findings.push({ type: 'negative', text: 'Add more results-oriented language. Focus on outcomes and achievements rather than listing responsibilities.' });
  }

  // Calculate score
  let score = 30; // base
  score += Math.min(metricsRatio * 100, 30);
  score += Math.min(actionVerbRatio * 80, 20);
  score += Math.min(resultCount * 2, 20);

  return { name: 'Impact & Achievements', score: Math.min(Math.round(score), 100), findings };
}

function scoreFormatting(text: string): CategoryResult {
  const lines = text.split('\n');
  const nonEmptyLines = lines.filter((l) => l.trim().length > 0);
  const findings: Finding[] = [];

  // Check for section headers
  const lowerText = text.toLowerCase();
  const foundSections = SECTION_HEADERS.filter((h) => {
    const regex = new RegExp(`(^|\\n)\\s*${h}\\s*($|\\n|:|-|\\|)`, 'i');
    return regex.test(lowerText);
  });

  if (foundSections.length >= 4) {
    findings.push({ type: 'positive', text: `Well-organized with ${foundSections.length} clear sections detected (${foundSections.slice(0, 4).join(', ')}).` });
  } else if (foundSections.length >= 2) {
    findings.push({ type: 'neutral', text: `${foundSections.length} sections detected. Consider adding more clearly labeled sections (Experience, Education, Skills, Summary).` });
  } else {
    findings.push({ type: 'negative', text: 'Few section headers found. Use clear headings like "Experience," "Education," and "Skills" to organize your resume.' });
  }

  // Check for bullet points
  const bulletLines = nonEmptyLines.filter((l) => /^\s*[-*\u2022\u25CF\u25CB\u2023\u2043\u25AA\u25AB\u2013\u2014\u27A4>]/.test(l.trim()) || /^\s*\d+[.)]\s/.test(l.trim()));
  const bulletRatio = nonEmptyLines.length > 0 ? bulletLines.length / nonEmptyLines.length : 0;

  if (bulletRatio > 0.3) {
    findings.push({ type: 'positive', text: 'Good use of bullet points for readability and scannability.' });
  } else if (bulletRatio > 0.1) {
    findings.push({ type: 'neutral', text: 'Some bullet points found. Use bullet points more consistently under each role for better readability.' });
  } else {
    findings.push({ type: 'negative', text: 'Very few bullet points detected. Use bullet points (not paragraphs) to list achievements under each role.' });
  }

  // Check length (word count)
  const wordCount = text.split(/\s+/).filter((w) => w.length > 0).length;
  if (wordCount >= 200 && wordCount <= 800) {
    findings.push({ type: 'positive', text: `Resume length is appropriate (~${wordCount} words, typical for a 1-2 page resume).` });
  } else if (wordCount < 200) {
    findings.push({ type: 'negative', text: `Resume seems too short (~${wordCount} words). A typical resume is 300-700 words. Add more detail about your achievements.` });
  } else {
    findings.push({ type: 'negative', text: `Resume may be too long (~${wordCount} words). Consider trimming to focus on the most impactful and recent content.` });
  }

  let score = 20;
  score += Math.min(foundSections.length * 8, 32);
  score += bulletRatio > 0.3 ? 25 : bulletRatio > 0.1 ? 15 : 5;
  score += (wordCount >= 200 && wordCount <= 800) ? 23 : (wordCount >= 100 && wordCount <= 1000) ? 12 : 3;

  return { name: 'Formatting & Structure', score: Math.min(Math.round(score), 100), findings };
}

function scoreATS(text: string): CategoryResult {
  const findings: Finding[] = [];
  const lowerText = text.toLowerCase();

  // Check for ATS-unfriendly characters
  const specialChars = text.match(/[^\x00-\x7F]/g) || [];
  const tableChars = text.match(/[|+]{3,}/g) || [];

  if (specialChars.length === 0 && tableChars.length === 0) {
    findings.push({ type: 'positive', text: 'No problematic special characters or table formatting detected -- ATS systems should parse this cleanly.' });
  } else if (specialChars.length <= 5) {
    findings.push({ type: 'neutral', text: `${specialChars.length} non-ASCII characters found. Some ATS systems may not parse these correctly. Stick to standard characters.` });
  } else {
    findings.push({ type: 'negative', text: `${specialChars.length} special characters detected. Replace fancy bullets, icons, and symbols with standard ASCII characters for better ATS compatibility.` });
  }

  // Check for skills section
  const hasSkills = SKILLS_HEADERS.some((h) => {
    const regex = new RegExp(`(^|\\n)\\s*${h}\\s*($|\\n|:|-|\\|)`, 'i');
    return regex.test(lowerText);
  });

  if (hasSkills) {
    findings.push({ type: 'positive', text: 'Skills section found -- ATS systems specifically look for this section to match keywords.' });
  } else {
    findings.push({ type: 'negative', text: 'No dedicated "Skills" section found. ATS systems rely heavily on a clear skills section to match you to job requirements.' });
  }

  // Check for standard section names (ATS looks for these)
  const hasExperience = EXPERIENCE_HEADERS.some((h) => {
    const regex = new RegExp(`(^|\\n)\\s*${h}\\s*($|\\n|:|-|\\|)`, 'i');
    return regex.test(lowerText);
  });
  const hasEducation = EDUCATION_HEADERS.some((h) => {
    const regex = new RegExp(`(^|\\n)\\s*${h}\\s*($|\\n|:|-|\\|)`, 'i');
    return regex.test(lowerText);
  });

  const atsStandardSections = [hasExperience, hasEducation, hasSkills].filter(Boolean).length;
  if (atsStandardSections === 3) {
    findings.push({ type: 'positive', text: 'All three core ATS sections found (Experience, Education, Skills) -- this is the ideal structure.' });
  } else if (atsStandardSections >= 1) {
    const missing = [];
    if (!hasExperience) missing.push('Experience');
    if (!hasEducation) missing.push('Education');
    if (!hasSkills) missing.push('Skills');
    findings.push({ type: 'negative', text: `Missing ATS-critical section(s): ${missing.join(', ')}. Add these with standard header names for best parsing.` });
  } else {
    findings.push({ type: 'negative', text: 'No standard ATS sections detected. Use exact headers: "Experience," "Education," and "Skills" so ATS systems can parse your resume.' });
  }

  let score = 15;
  score += specialChars.length === 0 ? 25 : specialChars.length <= 5 ? 15 : 5;
  score += hasSkills ? 20 : 0;
  score += hasExperience ? 20 : 0;
  score += hasEducation ? 20 : 0;

  return { name: 'ATS Readiness', score: Math.min(Math.round(score), 100), findings };
}

function scoreWordChoice(text: string): CategoryResult {
  const findings: Finding[] = [];
  const lowerText = text.toLowerCase();

  // Check for weak phrases
  const foundWeakPhrases = WEAK_PHRASES.filter((phrase) => lowerText.includes(phrase));
  if (foundWeakPhrases.length === 0) {
    findings.push({ type: 'positive', text: 'No weak phrases like "responsible for" or "duties included" found -- your language sounds confident.' });
  } else if (foundWeakPhrases.length <= 2) {
    findings.push({ type: 'neutral', text: `Found ${foundWeakPhrases.length} weak phrase(s): "${foundWeakPhrases.join('", "')}". Replace with strong action verbs that show ownership.` });
  } else {
    findings.push({ type: 'negative', text: `Found ${foundWeakPhrases.length} weak phrases: "${foundWeakPhrases.slice(0, 3).join('", "')}". These make you sound passive. Replace "responsible for X" with "led X" or "delivered X."` });
  }

  // Check for buzzwords
  const foundBuzzwords = BUZZWORDS.filter((bw) => {
    const regex = new RegExp(`\\b${bw}\\b`, 'i');
    return regex.test(lowerText);
  });
  if (foundBuzzwords.length === 0) {
    findings.push({ type: 'positive', text: 'No cliche buzzwords detected -- your resume sounds authentic and specific.' });
  } else if (foundBuzzwords.length <= 2) {
    findings.push({ type: 'neutral', text: `${foundBuzzwords.length} buzzword(s) found: "${foundBuzzwords.join('", "')}". Consider replacing with more specific, concrete language.` });
  } else {
    findings.push({ type: 'negative', text: `${foundBuzzwords.length} buzzwords found: "${foundBuzzwords.slice(0, 3).join('", "')}". These are overused and vague. Replace with specific accomplishments.` });
  }

  // Check for first-person pronouns (should NOT be in resume)
  const pronounPattern = /\b(I|me|my|myself|we|our)\b/g;
  const pronounMatches = text.match(pronounPattern) || [];
  if (pronounMatches.length === 0) {
    findings.push({ type: 'positive', text: 'Correctly written in implied first person (no "I" or "my") -- this is proper resume style.' });
  } else if (pronounMatches.length <= 3) {
    findings.push({ type: 'neutral', text: `Found ${pronounMatches.length} first-person pronoun(s). Resumes should omit "I," "my," and "me" -- start bullets with action verbs instead.` });
  } else {
    findings.push({ type: 'negative', text: `Found ${pronounMatches.length} first-person pronouns. Remove all instances of "I," "my," and "me" and start each bullet with an action verb.` });
  }

  let score = 40;
  score -= foundWeakPhrases.length * 8;
  score -= foundBuzzwords.length * 5;
  score -= pronounMatches.length * 4;

  // Bonus for strong action verbs
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const actionVerbLines = lines.filter((line) => {
    const firstWord = line.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '');
    return STRONG_ACTION_VERBS.includes(firstWord);
  });
  score += Math.min(actionVerbLines.length * 3, 30);

  // Word variety bonus
  const words = lowerText.split(/\s+/).filter((w) => w.length > 3);
  const uniqueWords = new Set(words);
  const varietyRatio = words.length > 0 ? uniqueWords.size / words.length : 0;
  score += varietyRatio > 0.6 ? 30 : varietyRatio > 0.4 ? 20 : 10;

  return { name: 'Word Choice', score: Math.max(0, Math.min(Math.round(score), 100)), findings };
}

function scoreCompleteness(text: string): CategoryResult {
  const findings: Finding[] = [];
  const lowerText = text.toLowerCase();

  // Check for contact information patterns
  const hasEmail = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(text);
  const hasPhone = /(\+?\d{1,3}[-.\s]?)?\(?\d{2,4}\)?[-.\s]?\d{3,4}[-.\s]?\d{3,4}/.test(text);
  const hasLinkedIn = /linkedin\.com|linkedin/i.test(text);
  const hasLocation = /[A-Z][a-z]+,\s*[A-Z]{2}|[A-Z][a-z]+,\s*[A-Za-z]+/.test(text);

  const contactItems = [hasEmail, hasPhone, hasLinkedIn, hasLocation].filter(Boolean);
  if (contactItems.length >= 3) {
    findings.push({ type: 'positive', text: `Contact information looks complete (${contactItems.length}/4 items: ${[hasEmail && 'email', hasPhone && 'phone', hasLinkedIn && 'LinkedIn', hasLocation && 'location'].filter(Boolean).join(', ')}).` });
  } else if (contactItems.length >= 1) {
    const missing = [];
    if (!hasEmail) missing.push('email');
    if (!hasPhone) missing.push('phone');
    if (!hasLinkedIn) missing.push('LinkedIn URL');
    if (!hasLocation) missing.push('city/state');
    findings.push({ type: 'neutral', text: `Some contact info found but consider adding: ${missing.join(', ')}. Recruiters need easy ways to reach you.` });
  } else {
    findings.push({ type: 'negative', text: 'No contact information detected. Include your email, phone number, LinkedIn URL, and city/state at the top of your resume.' });
  }

  // Check for core sections
  const hasExperience = EXPERIENCE_HEADERS.some((h) => {
    const regex = new RegExp(`(^|\\n)\\s*${h}\\s*($|\\n|:|-|\\|)`, 'i');
    return regex.test(lowerText);
  });
  const hasEducation = EDUCATION_HEADERS.some((h) => {
    const regex = new RegExp(`(^|\\n)\\s*${h}\\s*($|\\n|:|-|\\|)`, 'i');
    return regex.test(lowerText);
  });
  const hasSkills = SKILLS_HEADERS.some((h) => {
    const regex = new RegExp(`(^|\\n)\\s*${h}\\s*($|\\n|:|-|\\|)`, 'i');
    return regex.test(lowerText);
  });
  const hasSummary = /(^|\n)\s*(summary|professional summary|objective|career objective|profile)\s*($|\n|:|-|\|)/i.test(lowerText);

  const coreSections = [hasExperience, hasEducation, hasSkills].filter(Boolean).length;
  if (coreSections === 3 && hasSummary) {
    findings.push({ type: 'positive', text: 'All essential sections present: Summary/Objective, Experience, Education, and Skills.' });
  } else if (coreSections >= 2) {
    const missing = [];
    if (!hasExperience) missing.push('Experience');
    if (!hasEducation) missing.push('Education');
    if (!hasSkills) missing.push('Skills');
    if (!hasSummary) missing.push('Summary/Objective');
    findings.push({ type: 'neutral', text: `Most key sections found. Consider adding: ${missing.join(', ')} for a more complete resume.` });
  } else {
    findings.push({ type: 'negative', text: 'Missing multiple essential sections. A complete resume should include: Summary, Experience, Education, and Skills.' });
  }

  // Check for dates (employment history should have dates)
  const datePatterns = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)\s*\.?\s*\d{2,4}|\b\d{1,2}\/\d{2,4}|\b20\d{2}\b|\b19\d{2}\b|\bpresent\b|\bcurrent\b/gi;
  const dateMatches = text.match(datePatterns) || [];
  if (dateMatches.length >= 4) {
    findings.push({ type: 'positive', text: `${dateMatches.length} date references found -- employment dates are clearly listed.` });
  } else if (dateMatches.length >= 1) {
    findings.push({ type: 'neutral', text: `Only ${dateMatches.length} date(s) found. Make sure each position and education entry includes start and end dates.` });
  } else {
    findings.push({ type: 'negative', text: 'No employment dates detected. Include date ranges (e.g., "Jan 2020 - Present") for each position.' });
  }

  let score = 10;
  score += contactItems.length * 8;
  score += hasExperience ? 18 : 0;
  score += hasEducation ? 15 : 0;
  score += hasSkills ? 15 : 0;
  score += hasSummary ? 10 : 0;
  score += Math.min(dateMatches.length * 3, 12);

  return { name: 'Completeness', score: Math.min(Math.round(score), 100), findings };
}

/* ------------------------------------------------------------------ */
/*  Main grading function                                              */
/* ------------------------------------------------------------------ */

function gradeResume(text: string): GradeResult {
  const impact = scoreImpact(text);
  const formatting = scoreFormatting(text);
  const ats = scoreATS(text);
  const wordChoice = scoreWordChoice(text);
  const completeness = scoreCompleteness(text);

  const categories = [impact, formatting, ats, wordChoice, completeness];

  // Weighted average: Impact 30%, Formatting 20%, ATS 20%, Word Choice 15%, Completeness 15%
  const weights = [0.30, 0.20, 0.20, 0.15, 0.15];
  const overall = Math.round(
    categories.reduce((sum, cat, i) => sum + cat.score * weights[i], 0)
  );

  let letterGrade: string;
  if (overall >= 97) letterGrade = 'A+';
  else if (overall >= 93) letterGrade = 'A';
  else if (overall >= 90) letterGrade = 'A-';
  else if (overall >= 87) letterGrade = 'B+';
  else if (overall >= 83) letterGrade = 'B';
  else if (overall >= 80) letterGrade = 'B-';
  else if (overall >= 77) letterGrade = 'C+';
  else if (overall >= 73) letterGrade = 'C';
  else if (overall >= 70) letterGrade = 'C-';
  else if (overall >= 60) letterGrade = 'D';
  else letterGrade = 'F';

  return { overall, letterGrade, categories };
}

/* ------------------------------------------------------------------ */
/*  Helper components                                                  */
/* ------------------------------------------------------------------ */

function getLetterGradeColor(grade: string): string {
  if (grade.startsWith('A')) return 'bg-green-500';
  if (grade.startsWith('B')) return 'bg-blue-500';
  if (grade.startsWith('C')) return 'bg-amber-500';
  return 'bg-red-500';
}

function FindingIcon({ type }: { type: Finding['type'] }) {
  if (type === 'positive') {
    return (
      <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  if (type === 'negative') {
    return (
      <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Resume writing tips data                                           */
/* ------------------------------------------------------------------ */

const RESUME_TIPS = [
  {
    title: 'Lead with Impact, Not Duties',
    description: 'Every bullet point should answer "so what?" Focus on outcomes and results rather than listing job responsibilities.',
  },
  {
    title: 'Quantify Everything You Can',
    description: 'Numbers grab attention. Revenue generated, percentage improvements, team size, projects completed -- metrics make your resume concrete.',
  },
  {
    title: 'Tailor for Each Application',
    description: 'Customize your resume for each role. Mirror the language in the job posting and emphasize the most relevant experience.',
  },
  {
    title: 'Keep It ATS-Friendly',
    description: 'Use standard section headers, avoid tables and graphics, and stick to common fonts. Many resumes are filtered by software before a human sees them.',
  },
  {
    title: 'Use the XYZ Formula',
    description: 'Structure bullets as: "Accomplished [X] as measured by [Y], by doing [Z]." For example: "Reduced customer churn by 15% by implementing a proactive outreach program."',
  },
  {
    title: 'Proofread Ruthlessly',
    description: 'A single typo can cost you an interview. Read your resume backward, use spell check, and have someone else review it before submitting.',
  },
];

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function ResumeScoreContent() {
  const [resumeText, setResumeText] = useState('');
  const [result, setResult] = useState<GradeResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  function handleGrade() {
    if (resumeText.trim().length < 50) return;
    setIsAnalyzing(true);
    // Small delay for UX -- feels like it's processing
    setTimeout(() => {
      setResult(gradeResume(resumeText));
      setIsAnalyzing(false);
    }, 800);
  }

  function handleReset() {
    setResult(null);
    setResumeText('');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Resume Grader"
        description="Paste your resume and get an instant score across 5 key dimensions. No sign-up, no AI -- 100% private, runs in your browser."
        category="Resume Analysis"
      />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Input section */}
        {!result && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Paste Your Resume Text
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Copy the text from your resume and paste it below. We analyze formatting patterns, word choice, and structure to score your resume across 5 categories.
            </p>

            <textarea
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder={`John Smith
john.smith@email.com | (555) 123-4567 | linkedin.com/in/johnsmith | New York, NY

Professional Summary
Results-driven software engineer with 5+ years of experience...

Experience
Senior Software Engineer | Acme Corp | Jan 2021 - Present
- Increased API performance by 40% by redesigning the caching layer
- Led a team of 6 engineers to deliver a $2M platform migration on time
...`}
              className="w-full h-72 p-4 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
            />

            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-gray-400">
                {resumeText.trim().length > 0
                  ? `${resumeText.split(/\s+/).filter((w) => w.length > 0).length} words`
                  : 'Minimum 50 characters'}
              </span>
              <button
                onClick={handleGrade}
                disabled={resumeText.trim().length < 50 || isAnalyzing}
                className="bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAnalyzing ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Analyzing...
                  </>
                ) : (
                  'Grade My Resume'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Results section */}
        {result && (
          <div className="space-y-8">
            {/* Overall score card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
                <ScoreGauge score={result.overall} label="Overall Score" size={220} />
                <div className="flex flex-col items-center sm:items-start">
                  <span className={`inline-block text-3xl font-extrabold text-white px-5 py-2 rounded-xl ${getLetterGradeColor(result.letterGrade)}`}>
                    {result.letterGrade}
                  </span>
                  <p className="text-sm text-gray-500 mt-2">Letter Grade</p>
                  <p className="text-gray-600 mt-3 text-sm max-w-xs text-center sm:text-left">
                    {result.overall >= 80
                      ? 'Your resume is strong. Fine-tune the areas below to make it even better.'
                      : result.overall >= 60
                        ? 'Your resume is decent but has clear areas for improvement. Address the findings below.'
                        : 'Your resume needs significant work. Focus on the critical issues highlighted below.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Category scores overview */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Score Breakdown</h3>
              <div className="space-y-5">
                {result.categories.map((cat) => (
                  <ProgressBar key={cat.name} value={cat.score} label={cat.name} />
                ))}
              </div>
            </div>

            {/* Detailed findings per category */}
            {result.categories.map((cat) => (
              <div key={cat.name} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{cat.name}</h3>
                  <span
                    className={`text-sm font-bold px-3 py-1 rounded-full ${
                      cat.score >= 70
                        ? 'bg-green-100 text-green-700'
                        : cat.score >= 40
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {cat.score}/100
                  </span>
                </div>
                <ul className="space-y-3">
                  {cat.findings.map((finding, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <FindingIcon type={finding.type} />
                      <span className="text-sm text-gray-700 leading-relaxed">{finding.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Start over */}
            <div className="text-center">
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Grade Another Resume
              </button>
            </div>

            {/* Tips section */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-blue-900 mb-6 flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Resume Writing Tips
              </h3>
              <div className="grid sm:grid-cols-2 gap-5">
                {RESUME_TIPS.map((tip, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-blue-100">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">{tip.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Want a higher score? Let AI optimize your resume."
              description="Our AI resume builder applies all of these best practices automatically -- strong action verbs, quantified achievements, ATS-friendly formatting, and more."
              buttonText="Build a Better Resume Now"
            />
          </div>
        )}

        {/* Educational section (shown when no results yet) */}
        {!result && (
          <div className="mt-12 space-y-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What Does This Tool Grade?</h3>
              <p className="text-gray-600 mb-6">
                Our resume grader analyzes your resume text across five critical dimensions that recruiters and hiring managers care about most:
              </p>
              <div className="space-y-4">
                {[
                  { name: 'Impact & Achievements', weight: '30%', desc: 'Do your bullet points show measurable results? Are you using strong action verbs and quantifying your accomplishments?' },
                  { name: 'Formatting & Structure', weight: '20%', desc: 'Is your resume well-organized with clear section headers, consistent bullet points, and an appropriate length?' },
                  { name: 'ATS Readiness', weight: '20%', desc: 'Will your resume survive Applicant Tracking Systems? We check for standard headers, clean formatting, and a skills section.' },
                  { name: 'Word Choice', weight: '15%', desc: 'Are you using confident, active language? We flag weak phrases like "responsible for" and overused buzzwords.' },
                  { name: 'Completeness', weight: '15%', desc: 'Does your resume include all essential elements -- contact info, experience, education, skills, and employment dates?' },
                ].map((item) => (
                  <div key={item.name} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-lg whitespace-nowrap">{item.weight}</span>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">{item.name}</h4>
                      <p className="text-sm text-gray-600 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                <strong>Sources &amp; Further Reading:</strong>{' '}
                <a href="https://www.indeed.com/career-advice/resumes-cover-letters/resume-tips" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: Top Resume Writing Tips</a>
                {' · '}
                <a href="https://hbr.org/2014/12/how-to-write-a-resume-that-stands-out" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">HBR: How to Write a Resume That Stands Out</a>
                {' · '}
                <a href="https://www.careeronestop.org/JobSearch/Resumes/ResumeGuide/TopResumeTips/tips-for-writing-your-resume.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop Resume Guide (U.S. DOL)</a>
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-blue-900 mb-6 flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Resume Writing Tips
              </h3>
              <div className="grid sm:grid-cols-2 gap-5">
                {RESUME_TIPS.map((tip, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-blue-100">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">{tip.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <ToolCTA
              headline="Go beyond grading -- build a perfect resume"
              description="Our AI resume builder creates ATS-optimized, achievement-focused resumes in minutes. Get a higher score without the guesswork."
              buttonText="Build My Resume"
            />
          </div>
        )}
      </div>
    </div>
  );
}
